/**
 * Map Ban Node - Player View
 *
 * Tactical elimination-style map ban phase. Dark, high-contrast map cards
 * with red strike-through on banned maps and team-based turn indicator.
 * Matches the ReadyUp/MapVote quality bar.
 */

import { getTeamNames } from "@rivaled/shared";
import { AnimatePresence, motion } from "framer-motion";
import { ImageOff } from "lucide-react";
import { useMemo, useState } from "react";
import { trpc } from "../../../lib/trpc";
import { cn } from "../../../lib/utils";
import { ErrorBanner } from "../../components/ErrorBanner";
import { NodeCard } from "../../components/NodeCard";
import { NodeHeader } from "../../components/NodeHeader";
import { useCurrentPlayerId } from "../../hooks/useCurrentPlayerId";
import type { NodePlayerViewProps } from "../types";

type MapBanState = {
  mapPool: string[];
  bannedMaps: string[];
  bansByTeam: Record<number, string[]>;
  currentTeam: number;
  banIndex: number;
  totalBans: number;
  mapDetails?: Record<string, { name: string; imageUrl: string | null }>;
};

export function MapBanView({
  nodeState,
  players: _players,
  context,
  onAction,
}: NodePlayerViewProps<MapBanState>) {
  const userId = useCurrentPlayerId();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const state = nodeState.state as MapBanState;
  const config = nodeState.config as { labels?: { title?: string } };
  const title = config.labels?.title ?? "Map Ban Phase";
  const teams = context.teams as Record<string, number> | undefined;

  const myTeam = teams?.[userId || ""] ?? 0;
  const isMyTurn = myTeam === state.currentTeam;

  // Seeded team names
  const teamNames = useMemo(
    () => getTeamNames(context.matchId, context.teamCount),
    [context.matchId, context.teamCount],
  );
  const getTeamName = (teamNum: number) => teamNames[teamNum] ?? `Team ${teamNum}`;

  // Fetch map details from tRPC, fall back to state-provided mapDetails (dev preview)
  const { data: mapsFromApi } = trpc.game.getMaps.useQuery(
    { gameId: (context.gameId as string) || "" },
    { enabled: !!(context.gameId as string) },
  );

  const getMapInfo = (mapId: string) => {
    const fromApi = mapsFromApi?.find((m) => m.id === mapId);
    if (fromApi) return { name: fromApi.name, imageUrl: fromApi.imageUrl ?? null };
    const fromState = state.mapDetails?.[mapId];
    if (fromState) return fromState;
    return { name: mapId, imageUrl: null };
  };

  const handleBan = async (mapId: string) => {
    if (!isMyTurn) return;

    setIsSubmitting(true);
    setError(null);

    const result = await onAction("ban", { mapId, userId });

    if (!result.success && result.error) {
      setError(result.error);
    }

    setIsSubmitting(false);
  };

  const remainingMaps = state.mapPool.filter((m) => !state.bannedMaps.includes(m));

  return (
    <NodeCard>
      <NodeHeader
        title={title}
        subtitle={
          <AnimatePresence mode="wait">
            <motion.p
              key={isMyTurn ? "your-turn" : "waiting"}
              initial={{ opacity: 0, y: 4 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -4 }}
              transition={{ duration: 0.2 }}
              className="text-sm @sm:text-base"
            >
              {isMyTurn ? (
                <span className="text-red-400 font-semibold">Your team&apos;s turn to ban</span>
              ) : (
                <span className="text-text-secondary">
                  Waiting for {getTeamName(state.currentTeam)} to ban...
                </span>
              )}
            </motion.p>
          </AnimatePresence>
        }
      />

      {/* Progress pill */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.15 }}
        className="text-center mb-6"
      >
        <div className="inline-flex items-center gap-3 px-4 py-2 rounded-lg bg-surface-800/80 border border-surface-700/50">
          <span className="font-display text-2xl font-bold text-text-primary tabular-nums">
            {state.banIndex}
          </span>
          <span className="text-surface-500 text-sm">/</span>
          <span className="font-display text-2xl font-bold text-text-primary tabular-nums">
            {state.totalBans}
          </span>
          <span className="text-xs uppercase tracking-wider text-text-secondary font-medium">
            bans
          </span>
          <div className="w-px h-5 bg-surface-500/40" />
          <span className="font-display text-lg font-bold text-brand-400 tabular-nums">
            {remainingMaps.length}
          </span>
          <span className="text-xs uppercase tracking-wider text-text-secondary font-medium">
            remain
          </span>
        </div>
      </motion.div>

      <ErrorBanner error={error} />

      {/* Map cards grid */}
      <div className="grid grid-cols-1 @md:grid-cols-2 @3xl:grid-cols-3 gap-4 @md:gap-5">
        {state.mapPool.map((mapId, index) => {
          const info = getMapInfo(mapId);
          const isBanned = state.bannedMaps.includes(mapId);
          const bannedByTeam = Object.entries(state.bansByTeam).find(([, bans]) =>
            bans.includes(mapId),
          )?.[0];

          return (
            <BanCard
              key={mapId}
              mapId={mapId}
              name={info.name}
              imageUrl={info.imageUrl}
              isBanned={isBanned}
              bannedByTeam={bannedByTeam != null ? Number(bannedByTeam) : null}
              bannedByTeamName={bannedByTeam != null ? getTeamName(Number(bannedByTeam)) : null}
              myTeam={myTeam}
              isMyTurn={isMyTurn}
              disabled={isSubmitting || !isMyTurn || isBanned}
              index={index}
              onBan={handleBan}
            />
          );
        })}
      </div>

      {/* Team bans summary — always visible */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.3 }}
        className="mt-6 flex flex-wrap items-center justify-center gap-3"
      >
        {Array.from({ length: context.teamCount ?? 2 }, (_, teamNum) => {
          const bans = state.bansByTeam[teamNum] ?? [];
          const banCount = bans.length;
          const isMyTeamRow = teamNum === myTeam;
          return (
            <div
              key={teamNum}
              className={cn(
                "flex items-center gap-2 px-3 py-2 rounded-lg",
                isMyTeamRow
                  ? "bg-brand-500/[0.06] border border-brand-500/20"
                  : "bg-surface-800/60 border border-surface-700/40",
              )}
            >
              <span
                className={cn(
                  "text-2xs font-display font-semibold uppercase tracking-wider",
                  isMyTeamRow ? "text-brand-400" : "text-surface-400",
                )}
              >
                {getTeamName(teamNum)}
              </span>
              {isMyTeamRow && <span className="text-[10px] text-brand-400">(you)</span>}
              {banCount === 0 ? (
                <span className="text-xs text-surface-500 italic">No bans yet</span>
              ) : (
                <span className="text-xs text-text-secondary">
                  {banCount} {banCount === 1 ? "ban" : "bans"}
                </span>
              )}
            </div>
          );
        })}
      </motion.div>
    </NodeCard>
  );
}

// ---------------------------------------------------------------------------
// Ban card component
// ---------------------------------------------------------------------------

type BanCardProps = {
  mapId: string;
  name: string;
  imageUrl: string | null;
  isBanned: boolean;
  bannedByTeam: number | null;
  bannedByTeamName: string | null;
  myTeam: number;
  isMyTurn: boolean;
  disabled: boolean;
  index: number;
  onBan: (mapId: string) => void;
};

function BanCard({
  mapId,
  name,
  imageUrl,
  isBanned,
  bannedByTeam,
  bannedByTeamName,
  myTeam,
  isMyTurn,
  disabled,
  index,
  onBan,
}: BanCardProps) {
  const delay = Math.min(0.1 + index * 0.06, 0.5);

  return (
    <motion.button
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay }}
      whileHover={!disabled ? { scale: 1.02 } : undefined}
      whileTap={!disabled ? { scale: 0.98 } : undefined}
      onClick={() => onBan(mapId)}
      disabled={disabled}
      className={cn(
        "group relative rounded-lg overflow-hidden text-left",
        "transition-all duration-200",
        "focus:outline-none focus:ring-2 focus:ring-red-400 focus:ring-offset-2 focus:ring-offset-surface-900",
        isBanned
          ? "ring-1 ring-red-500/20 cursor-default"
          : isMyTurn
            ? "ring-1 ring-surface-700/50 hover:ring-red-500/50 cursor-pointer"
            : "ring-1 ring-surface-700/50 cursor-not-allowed opacity-80",
      )}
    >
      {/* Map image */}
      <div className="relative aspect-video bg-surface-800 overflow-hidden">
        {imageUrl ? (
          <img
            src={imageUrl}
            alt={name}
            className={cn(
              "w-full h-full object-cover transition-all duration-300",
              isBanned
                ? "brightness-[0.35] saturate-0 grayscale"
                : isMyTurn
                  ? "brightness-75 saturate-90 group-hover:brightness-90 group-hover:saturate-100"
                  : "brightness-60 saturate-75",
            )}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-surface-800">
            <ImageOff size={32} className="text-surface-600" aria-hidden="true" />
          </div>
        )}

        {/* Bottom gradient for text readability */}
        <div className="absolute inset-x-0 bottom-0 h-3/4 bg-gradient-to-t from-black/90 via-black/50 to-transparent" />

        {/* Hover glow for bannable maps */}
        {!isBanned && isMyTurn && (
          <div className="absolute inset-0 bg-red-500/0 group-hover:bg-red-500/10 transition-colors duration-200" />
        )}

        {/* Map name — over image bottom */}
        <div className="absolute inset-x-0 bottom-0 p-3 @md:p-4">
          <p
            className={cn(
              "font-display font-bold text-lg @sm:text-xl leading-tight",
              isBanned
                ? "text-surface-400 line-through decoration-red-500/60 decoration-2"
                : "text-white",
            )}
            style={{ textShadow: isBanned ? "none" : "0 2px 6px rgba(0,0,0,0.7)" }}
          >
            {name}
          </p>
        </div>

        {/* Banned overlay */}
        <AnimatePresence>
          {isBanned && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="absolute inset-0 flex flex-col items-center justify-center"
            >
              {/* Diagonal strike lines */}
              <div className="absolute inset-0 overflow-hidden pointer-events-none">
                <div
                  className="absolute top-0 left-0 w-[150%] h-px bg-red-500/30 origin-top-left rotate-[25deg] translate-y-[40%]"
                  style={{ top: "40%" }}
                />
                <div
                  className="absolute top-0 left-0 w-[150%] h-px bg-red-500/20 origin-top-left rotate-[25deg] translate-y-[45%]"
                  style={{ top: "45%" }}
                />
              </div>

              <motion.div
                initial={{ scale: 0, rotate: -12 }}
                animate={{ scale: 1, rotate: -6 }}
                transition={{ type: "spring", stiffness: 300, damping: 20, delay: 0.1 }}
                className="relative"
              >
                <div className="px-4 py-1.5 bg-red-500/90 backdrop-blur-sm rounded-sm border border-red-400/30 shadow-[0_0_20px_rgba(239,68,68,0.3)]">
                  <span className="font-display font-bold text-white text-sm uppercase tracking-wider">
                    Banned
                  </span>
                </div>
              </motion.div>

              {bannedByTeam != null && (
                <motion.span
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.3 }}
                  className="mt-2 text-2xs uppercase tracking-wider text-red-300/70 font-medium"
                >
                  by {bannedByTeamName ?? `Team ${bannedByTeam}`}
                  {bannedByTeam === myTeam ? " (you)" : ""}
                </motion.span>
              )}
            </motion.div>
          )}
        </AnimatePresence>

        {/* Top accent line for active cards */}
        {!isBanned && isMyTurn && (
          <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-red-500/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
        )}
      </div>
    </motion.button>
  );
}
